import { Component, OnInit, OnChanges, SimpleChanges, DoCheck } from '@angular/core';
import { MultiSourceMultiValueWidget } from "../../base/MultiSourceMultiValueWidget";
import * as Highcharts from "highcharts";

import more from "highcharts/highcharts-more";
more(Highcharts);

import { WindRoseWidget, SharedSeries } from 'src/app/models/dashboard';

@Component({
  selector: 'app-wind-rose-widget',
  templateUrl: './wind-rose-widget.component.html',
  styleUrls: ['./wind-rose-widget.component.css']
})
export class WindRoseWidgetComponent extends MultiSourceMultiValueWidget {
  public override widget: WindRoseWidget;
  public offset: number;
  public speedUri: string;
  public directionUri: string;
  public speedData: { value: number, timeStamp: string }[];
  public directionData: { value: string, timeStamp: string }[];
  public windData: { speed: number, direction: number, timeStamp: string}[];
  public maxSpeed: number;
  private speedTags: any[] | null;
  private directionTags: any[] | null;

  public isConfigured: boolean;

  // highcharts
  public chart: Highcharts.Chart;
  public Highcharts: typeof Highcharts = Highcharts;
  public chartOptions: Highcharts.Options = {}; // required
  public chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
    this.chart = chart;
    // Display 
    this.chart.addSeries({} as any);
    this.chart.series[0].remove();
    this.chart.showLoading();
  };
  public updateFlag: boolean = false;
  public oneToOneFlag: boolean = true; // optional boolean, defaults to false

  liveDataOnly(): boolean {
    return false;
  }

  override eventTypes: string[]; 
  override timeSeriesEvent(type: string, uri: string): void {
    
  }

  widgetAfterInit() {
  }

  widgetDataAdded() {
  }

  widgetBeforeInit() {
    this.offset = - (new Date().getTimezoneOffset() * 60 * 1000);

    let c = <any>this.config;
    if (this.widget.windSegmentSize == null || this.widget.windSegmentSize < 1 || this.widget.windSegmentSize > 20){
      this.widget.windSegmentSize = 10;
    }

    if (c.timeSeriesList && c.timeSeriesList.length == 2){
      if (c.timeSeriesList[0].tagMatches && c.timeSeriesList[0].tagMatches.length > 0)
      {
        //if speed is looked at using tagmatches
        this.speedTags = c.timeSeriesList[0].tagMatches;
      }
      else {
        this.speedUri = c.timeSeriesList[0].uri;
      }

      if (c.timeSeriesList[1].tagMatches && c.timeSeriesList[1].tagMatches.length > 0)
      {
        //if direction is looked at using tagmatches
        this.directionTags = c.timeSeriesList[1].tagMatches;
      } 
      else {
        this.directionUri = c.timeSeriesList[1].uri;
      }
      
      this.isConfigured = true;
    } else {
      this.isConfigured = false;
    }

    this.chartOptions = {
      colors: ['#4572A7', '#AA4643', '#89A54E', '#80699B', '#3D96AE', '#DB843D', '#92A8CD', '#A47D7C', '#B5CA92'],
      chart: {
        polar: true,
        type: 'column',
        backgroundColor: 'transparent',
        reflow: true,
        height: null,
        margin: 5
      },
      loading: {
        hideDuration: 1000,
        showDuration: 1000
      },
      title: {
        text: ""
      },
      legend: {
        enabled: false,
        borderWidth: 0,
        margin: 5
      },
      tooltip: {
        pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.p}%</b><br/>',
        followPointer: true,
        shared: true
      },
      navigation:{
        buttonOptions:{
          enabled: false
        }
      },
      credits: {
        enabled: false
      },
      xAxis: {        
        tickmarkPlacement: 'on',
        categories: this.getDirections()
      },
      yAxis: {
        min: 0,
        endOnTick: false,
        showLastLabel: true,
        title: {
          text: ''
        },
        labels: {
          enabled: false,
          formatter: function() {
            return this.value + '%';
          }
        },
        reversedStacks: false
      },
      plotOptions: {
        series: {
          stacking: 'normal',
          shadow: false,
          pointPlacement: 'on'
        },
      }
    }
    
    this.chart?.hideLoading();
  }

  widgetDataLoadTimeout(){
    this.chart.hideLoading();
  }

  toggleFullscreen(){
    this.fullScreen = !this.fullScreen;
    this.chartOptions.chart.height = this.fullScreen ? window.innerHeight - 350 : this.widget.height;
    this.chart.update(this.chartOptions);
  }

  refresh(){
    this.chart.showLoading();
    this.loadComplete(true);
  }

  public override async loadData(): Promise<void> {
    //check if chart already exists so we can reset it
    if(this.chart) 
    {
      //remove all existing series from the chart
      while(this.chart.series.length > 0) 
      {
        this.chart.series[0].remove();
      }

      //reset colours back to OG
      (this.chart as any).colorCounter = 0;
    }
    return super.loadData(); //load in new data
  }

  widgetLiveDataLoaded(s: any) {

  }


  widgetDataLoaded(s: SharedSeries) {
    if (!s) return;
    
    this.chart?.hideLoading();
    if (!this.directionData || !this.speedData) {
      if ((this.speedUri && s.uri === this.speedUri) || (this.speedUri == null && s.index == 0)) {
        // Speed
        const filteredSpeeds = s.values.filter(speed => typeof speed.value === 'number');
        this.maxSpeed = 0;

        this.speedData = [];
        this.speedData = filteredSpeeds.map(speed => {
          if (this.maxSpeed < speed.value){
            this.maxSpeed = speed.value;
          }
          return {
            value: speed.value,
            timeStamp: speed.timeStamp
          }
        });
      } else if ((this.directionUri && s.uri === this.directionUri) || (this.directionUri == null && s.index == 1)) {
        // Wind Direction
        const filteredDirections = s.values.filter(direction => typeof direction.value === 'number');

        this.directionData = [];
        this.directionData = filteredDirections.map(direction => {
          return {
            value: direction.value,
            timeStamp: direction.timeStamp
          }
        });
      }
      else if (s.metaData && (this.speedTags || this.directionTags)) //has metadata and speedtags or direction tags so loop both and check for matchy 
      {
        let speedCount = 0;
        let directionCount = 0;

        // Speed
        this.speedTags.forEach((tagSet) => {
          if (tagSet.tagName in s.metaData && tagSet.tagValue === s.metaData[tagSet.tagName]) {
            speedCount++;
          };

          if (speedCount === Object.keys(this.speedTags).length) {
            //all speed tags were found on current series
            const filteredSpeeds = s.values.filter(speed => typeof speed.value === 'number');
            this.maxSpeed = 0;
            this.speedData = [];
            this.speedData = filteredSpeeds.map(speed => {
              if (this.maxSpeed < speed.value) {
                this.maxSpeed = speed.value;
              }
              return {
                value: speed.value,
                timeStamp: speed.timeStamp
              }
            });
          }
        });

        //Direction
        this.directionTags.forEach((tagSet) => {
          if (tagSet.tagName in s.metaData && tagSet.tagValue === s.metaData[tagSet.tagName]) {
            directionCount++;
          };

          if (directionCount === Object.keys(this.directionTags).length) {
            //all direction tags were found on current series
            const filteredDirections = s.values.filter(direction => typeof direction.value === 'number');
            this.directionData = [];
            this.directionData = filteredDirections.map(direction => {
              return {
                value: direction.value,
                timeStamp: direction.timeStamp
              }
            });
          }

        });
      }
    }

    //both direction data and speedata have been found
    if (this.directionData && this.speedData) {
      this.windData = [];

      const directions = this.getDirections();
      let speeds: number[] = [];

      // group the data into speed and direction
      for (let i = 0; i < this.speedData.length; i++) {
        let speed = this.speedData[i];
        let direction = this.findClosest(this.directionData, speed.timeStamp);
        
        if (direction) {
          let speedCategory = this.getSpeedCategory(speed.value, this.widget.windSegmentSize);
          this.windData.push({ speed: speedCategory, direction: direction.value, timeStamp: speed.timeStamp });
          if (!speeds.includes(speedCategory)){
            speeds.push(speedCategory);
          }
        }
      }

      // Sort Wind Data
      speeds = speeds.sort((a, b) => a - b);

      // Enable legend
      this.chart.legend.update({ enabled: this.widget.showLegend });

      // group by wind speed
      for (let i = 0; i < speeds.length; i++) {
        const speed = speeds[i];
        const chartData: Highcharts.SeriesXrangeOptions = { name : `${speed} - ${speed + this.widget.windSegmentSize}`, data: [], type: undefined };
        // find all matches
        const matchingSpeeds = this.windData.filter(w => w.speed === speed);

        // loops each direction, finding speed
        for (let j = 0; j < directions.length; j++) {
          const direction = directions[j];
          const allSpeedsByDirection = this.windData.filter(w => this.getDirectionByDegree(w.direction) === direction);
          const matchingSpeedsByDirection = matchingSpeeds.filter(w => this.getDirectionByDegree(w.direction) === direction);

          // const speedOcurranceOverall = Math.round(matchingSpeedsByDirection.length / matchingSpeeds.length * 10000)/100;
          const speedOcurranceOverall = Math.round(matchingSpeedsByDirection.length / matchingSpeeds.length * 100) || 0;
          const speedOccurangeByDirection: number = Math.round(matchingSpeedsByDirection.length / allSpeedsByDirection.length * 100) || 0;

          chartData.data.push({ y: speedOcurranceOverall, p: speedOccurangeByDirection } as any);

          // debug
          // console.log(`Overall - ${direction} (${speed}): ${matchingSpeedsByDegrees.length} is what percentage of ${matchingSpeeds.length} = ${speedOcurranceOverall}`);
          // console.log(`Direction - ${direction} (${speed}): ${matchingSpeedsByDegrees.length} is what percentage of ${speedsByDegrees.length} = ${speedOccurangeByDirection}`);
          // console.log("--------------------")
        }
        
        // add data group by speed to chart
        this.chart.addSeries(chartData);
      }
      // After chart is series, wipe the speed & direction data that was used.
      this.speedData = this.directionData = null;

      this.chart?.hideLoading();
    }    
  };

  override widgetDataLoadComplete(): void {
      
  }

  getSpeedCategory(speed: number, segments: number): number { 
    return Math.round(speed / segments) * segments;
  }

  findClosest(directions, timeStamp: string) {
    var match = directions[0];
    for (let i = 0; i < directions.length; i++) {
      var direction = directions[i];
      if (direction.timeStamp <= timeStamp){
        match = direction;
      } else {
        return match;
      }
    }
    return null;
  }

  getDirections(): string[] {
    return  ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  }

  // Convert degrees to cardinal direction
  getDirectionByDegree(degrees: number): string {
    let i = (degrees + 11.25)/22.5;
    let directions = this.getDirections();
    let directionInDegrees = directions[Math.floor(i % 16)];
    return directionInDegrees;
  }
}
